export type Image = {
  alt: string
  sizes: {
    large: string
    largeHeight: number
    largeWidth: number
    medium: string
    mediumHeight: number
    mediumWidth: number
    mediumLarge: string
    mediumLargeHeight: number
    mediumLargeWidth: number
    small: string
    smallHeight: number
    smallWidth: number
    thumbnail: string
    thumbnailHeight: number
    thumbnailWidth: number
  }
}

export type AffiliateProfileImage = {
  size?: number
  lastModifiedDate?: string
  url?: string
}

export enum Certification {
  Level1 = 'L1',
}

export enum Role {
  LOR = 'lor',
  Coach = 'coach',
  Manager = 'manager',
}

export enum InvitationStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Rejected = 'rejected',
  Revoked = 'revoked',
}

export type AffiliateImages = {
  primary?: {
    url?: string
  }
  gallery?: {
    url?: string
    order?: number
  }[]
  coaches?: {
    id: number
    image?: {
      url?: string
    }
  }[]
  logo?: {
    url?: string
  }
  schedule?: {
    url?: string
  }
}

export type User = {
  id: number
  firstName: string
  lastName: string
  email: string
  phone?: string
  city?: string
  address?: string
}

export type UserDetail = {
  userId: number
  creationDate: string
  lastModifiedDate?: string
  gender?: string
  legalName?: string
  profileImageUrl?: string
  hideBenchmarks?: boolean
  email?: string
  emailVerified?: boolean
  accountStatus?: string
  lastLoginDate?: string
  firstName?: string
  lastName?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
  phoneCountryCode?: string
  phoneNumber?: string
  dob?: string
  sex?: string
  height?: number
  heightUnit?: string
  weight?: number
  weightUnit?: string
  hideProfile?: boolean
  permissions?: any[]
  certifications?: AffiliateMemberCertification[]
}

export type Teammate = {
  isPublic: boolean
  order?: number
  profileImageUrl?: string
  id: number
  roles: Role[]
  certifications: AffiliateMemberCertification[]
  email: string
  firstName: string
  lastName: string
}

export type Invitation = {
  email: string
  name: string
  id: number
  roles: Role[]
  token: string
  certifications: AffiliateMemberCertification[]
}

export type Invitee = {
  email: string
  id: number
  firstName: string
  lastName: string
  certifications?: AffiliateMemberCertification[]
}

export type ImageSizes = {
  hero?: string
  heroLarge?: string
  heroLargeWidth?: number
  heroWidth?: number
  large: string
  largeWidth: number
  medium: string
  mediumWidth: number
  small: string
  smallWidth: number
  thumbnail?: string
}

export type FullWidthPromoImage = {
  alt?: string
  src: ImageSizes
}

export type MemberReorder = {
  id: number
  order: number
}

export type FlexibleImageSrc = string | ImageSizes

export enum AffiliatePaymentStatus {
  Current = 'Current',
  PastDue1 = 'Past Due 1-30',
  PastDue30Plus = 'Past Due +30',
}

export enum AffiliateLicenseStatus {
  Current = 'Current',
  Unsigned1 = 'Unsigned 1-30',
  Unsigned30Plus = 'Unsigned +30',
}

export enum AffiliateCredentialStatus {
  Current = 'Current',
  Expired = 'Expired',
  ExpiredRegistered = 'Expired but Registered',
}

export type AffiliateStandingDetails = {
  paymentStatus: AffiliatePaymentStatus
  paymentDueDate: string
  licenseStatus: AffiliateLicenseStatus
  licenseRenewalDate: string
  credentialStatus: AffiliateCredentialStatus
  credentialExpirationDate: string
}

export type Affiliate = {
  name?: string
  city?: string
  stateCode?: string
  countryCode?: string
  address?: string
  lorId?: number
  groupId?: number
  bio?: string
  displayOnMap?: boolean
  coordinates?: string
  web?: string
  settings?: object
  slug?: string
  lat?: string
  lng?: string
  effectiveDate?: string
  lorUserId?: number
  lor?: {
    userId?: number
    firstName?: string
    lastName?: string
    email?: string
    id?: number
    creationDate?: string
    lastModifiedDate?: string
    roundtablesInterested?: boolean
    contactPhoneCountryCode?: string
    contactPhone?: string
    address1?: string
    address2?: string
    city?: string
    state?: string
    countryCode?: string
    postalCode?: string
    contactEmail: string
    country?: string
  }
  images?: AffiliateImages
  id?: number
  contactEmail?: string
  contactPhone?: string
  goodStanding?: boolean
  address1?: string
  address2?: string
  state?: string
  country?: string
  postalCode?: string
  insuranceExp?: string
  standingDetails?: AffiliateStandingDetails
}

export type MyAffiliate = {
  affiliateId: number
  affiliateName: string
  groupId: number
  affiliateStanding: 'good' | 'bad'
  roles: string[]
  affiliateCountryCode: string
  affiliateEffectiveDate: string
  affiliateWebsite: string
  affiliateContactEmail: string
  affiliateAddress: string
  affiliateState: string
  affiliatePostalCode: string
  financialStanding: boolean
  financialStatus: string
  standingDetails?: AffiliateStandingDetails
}

export type AffiliateMember = {
  isPublic: boolean
  order?: number
  profileImageUrl?: string
  id: number
  roles: Role[]
  certifications: AffiliateMemberCertification[]
  email: string
  firstName: string
  lastName: string
}

export type AffiliateMemberCertification = {
  level: string
  active: boolean
  email: string
  userId: number
  expiration?: string
}

export type CombinedAffiliate = Affiliate &
  MyAffiliate & { members: AffiliateMember[] }

export type DashboardHighlight = {
  headlineText: string
  bodyText: string
  link: { url: string; text: string }
  image
  roleVisibility: string[] | Role[]
  useCustomLink?: boolean
  customLink?: string
  customLinkText?: string
}

export interface ShopifyCart {
  attributes: Attribute[]
  buyerIdentity: BuyerIdentity
  checkoutUrl: string
  createdAt: string
  estimatedCost: EstimatedCost
  id: string
  lines: Lines
  updatedAt: string
}

export type RawCart = {
  cartTotal: string
  externalCartId: string
  id: string
  shopifyCart?: ShopifyCart
  userId: number
}

export interface Attribute {
  key: string
  value: string
}

// typed as any because in the response I got the values came back as null
export interface BuyerIdentity {
  countryCode: any
  customer: any
  email: any
  phone: any
}

export interface EstimatedCost {
  subtotalAmount: SubtotalAmount
  totalAmount: TotalAmount
  totalDutyAmount: any
  totalTaxAmount: any
}

export interface SubtotalAmount {
  amount: string
  currencyCode: string
}

export interface TotalAmount {
  amount: string
  currencyCode: string
}

export interface Lines {
  edges: Edge[]
}

export interface Edge {
  node?: LineItem
}

export interface LineItem {
  attributes: Attribute[]
  id: string
  merchandise: Merchandise
  quantity: number
}

export interface Merchandise {
  id: string
}

export type RawCheckoutUrl = {
  multipassCheckoutUrl: string
}

export enum RawRegistrationType {
  SEMINAR = 'seminar',
  OC = 'oc',
  LMS = 'lms',
}

export interface Resources {
  contentApi: string
}

export interface Rules {
  externalCourseIdentifier: number
  externalRegistration: boolean
  externalRegistrationService: string
}

export interface Course {
  id: number
  resources: Resources
  rules?: Rules
  skuCode: string
  title: string
  uuid: string
}

export interface RawRegistration {
  completedDate?: string
  course: Course
  courseId: number
  courseStatus?: string
  creationDate: string
  expiration?: string
  id: number
  lastModifiedDate?: string
  preferredAffiliateId?: string
  registrationStatus: string
  startedDate?: string
  type: RawRegistrationType
  userId: number
}
